import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  createMovement, updateMovement, uploadMovementImage, fileUrl, enrichMovement,
} from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Upload, ImageOff, Loader2, Sparkles } from "lucide-react";

const EMPTY = {
  movement_id: "", mvmnt_mfg: "", movement_type: "", size: "", jewels: "", bph: "",
  lift_angle: "",
  protection: "", year_introduced: "", year_discontinued: "",
  hour_hand_mm: "", minute_hand_mm: "", second_hand_mm: "",
  description: "", image_path: "", dial_side_path: "", notes: "", source_url: "",
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const str = (v) => (v === "" || v === null || v === undefined ? null : String(v));

export default function MovementFormSheet({ open, onOpenChange, movement, validation = {}, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Per-slot upload state so only the slot that's uploading shows the spinner.
  const [uploading, setUploading] = useState({ image_path: false, dial_side_path: false });
  const [enriching, setEnriching] = useState(false);
  const [enrichSource, setEnrichSource] = useState(null);
  const bridgeInput = useRef(null);
  const dialInput = useRef(null);

  useEffect(() => {
    if (movement) {
      const seed = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => {
        seed[k] = movement[k] === null || movement[k] === undefined ? "" : movement[k];
      });
      setData(seed);
    } else {
      setData(EMPTY);
    }
  }, [movement, open]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleEnrich = async () => {
    const cal = (data.movement_id || "").trim();
    if (!cal) {
      toast.error("Enter a caliber ID first");
      return;
    }
    setEnriching(true);
    setEnrichSource(null);
    try {
      const res = await enrichMovement(cal, data.mvmnt_mfg || null);
      if (!res || res.found === false) {
        toast.error(`No reference data found for "${cal}"`);
        return;
      }
      // Fill only blank fields, never overwrite — user's data wins.
      setData((d) => {
        const next = { ...d };
        const fields = ["mvmnt_mfg", "movement_type", "size", "jewels", "bph", "lift_angle", "protection",
          "year_introduced", "year_discontinued", "description", "source_url"];
        let filled = 0;
        fields.forEach((f) => {
          const cur = next[f];
          const newVal = res[f];
          if ((cur === "" || cur === null || cur === undefined) && newVal !== null && newVal !== undefined && newVal !== "") {
            next[f] = String(newVal);
            filled += 1;
          }
        });
        if (filled === 0) {
          toast.info("All fields already filled — nothing to add");
        } else {
          toast.success(`Pre-filled ${filled} field${filled === 1 ? "" : "s"}`);
        }
        return next;
      });
      if (res.source_url) setEnrichSource(res.source_url);
    } catch (err) {
      toast.error(errorMessage(err, "Enrichment failed"));
    } finally {
      setEnriching(false);
    }
  };

  const handleUpload = (slot, ref) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading((u) => ({ ...u, [slot]: true }));
    try {
      const r = await uploadMovementImage(f);
      set(slot, r.path);
      toast.success(`${slot === "dial_side_path" ? "Dial side" : "Bridge side"} image uploaded`);
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setUploading((u) => ({ ...u, [slot]: false }));
      if (ref.current) ref.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.movement_id || !data.movement_id.trim()) {
      toast.error("Movement ID is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        movement_id: str(data.movement_id),
        mvmnt_mfg: str(data.mvmnt_mfg),
        movement_type: str(data.movement_type),
        size: str(data.size),
        jewels: str(data.jewels),
        bph: num(data.bph),
        lift_angle: num(data.lift_angle),
        protection: str(data.protection),
        year_introduced: num(data.year_introduced),
        year_discontinued: num(data.year_discontinued),
        hour_hand_mm: num(data.hour_hand_mm),
        minute_hand_mm: num(data.minute_hand_mm),
        second_hand_mm: num(data.second_hand_mm),
        description: str(data.description),
        image_path: str(data.image_path),
        dial_side_path: str(data.dial_side_path),
        notes: str(data.notes),
        source_url: str(data.source_url),
      };
      if (movement?.id) {
        await updateMovement(movement.id, payload);
        toast.success("Movement updated");
      } else {
        await createMovement(payload);
        toast.success("Movement catalogued");
      }
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  // Bridge side (train / movement side) on the LEFT, Dial side on the RIGHT.
  // Keeps `image_path` as the canonical "thumbnail for list views" so existing
  // records with only a bridge-side shot surface correctly in the Movements list.
  const bridgeImg = fileUrl(data.image_path);
  const dialImg = fileUrl(data.dial_side_path);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[#FBF3D9] border-l border-[#8B6A1F]/45 text-[#0A0604] overflow-y-auto"
        data-testid="movement-form-sheet"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="font-serif-display text-3xl text-[#0A0604]">
            {movement ? "Edit Movement" : "New Movement"}
          </SheetTitle>
          <SheetDescription className="text-[#3A2A10] text-xs uppercase tracking-[0.25em]">
            Caliber specification
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="movement-form">
          <Section title="Photographs">
            <div className="text-[10px] text-[#3A2A10] mb-3 leading-relaxed">
              Two shots of the movement out of its case with no dial mounted —
              Bridge side (train / balance visible) on the left, Dial side
              (motion work / hour wheel visible) on the right. The Bridge side
              image doubles as the thumbnail in the Movements list.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MovementImageSlot
                label="Bridge Side"
                testidPrefix="mvmnt-bridge"
                img={bridgeImg}
                uploading={uploading.image_path}
                onPick={() => bridgeInput.current?.click()}
                onClear={() => set("image_path", "")}
                inputRef={bridgeInput}
                onFile={handleUpload("image_path", bridgeInput)}
              />
              <MovementImageSlot
                label="Dial Side"
                testidPrefix="mvmnt-dial"
                img={dialImg}
                uploading={uploading.dial_side_path}
                onPick={() => dialInput.current?.click()}
                onClear={() => set("dial_side_path", "")}
                inputRef={dialInput}
                onFile={handleUpload("dial_side_path", dialInput)}
              />
            </div>
          </Section>

          <Section title="Identity">
            <div className="bg-[#2B2214]/[0.04] border border-[#8B6A1F]/30 rounded-sm p-3 mb-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">
                Reference Lookup
              </div>
              <div className="flex flex-wrap gap-2 mb-2.5">
                <a
                  href={EMMY_SEARCH(data.movement_id, data.mvmnt_mfg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-[11px] uppercase tracking-wider text-[#3A2A10] hover:bg-[#F4E9C9]"
                  data-testid="mvmnt-launchpad-emmy"
                >
                  Emmy Watch →
                </a>
                <a
                  href={RANFFT_SEARCH(data.movement_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-[11px] uppercase tracking-wider text-[#3A2A10] hover:bg-[#F4E9C9]"
                  data-testid="mvmnt-launchpad-ranfft"
                >
                  Ranfft.org →
                </a>
                <Button
                  type="button"
                  onClick={handleEnrich}
                  disabled={enriching || !data.movement_id?.trim()}
                  variant="outline"
                  className="h-8 text-[11px] uppercase tracking-wider border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.08] rounded-sm disabled:opacity-40"
                  data-testid="mvmnt-form-enrich"
                >
                  {enriching
                    ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> AI…</>
                    : <><Sparkles size={12} className="mr-1.5" /> AI Fill</>}
                </Button>
              </div>
              <PasteHelper
                onApply={(fields) => {
                  Object.entries(fields).forEach(([k, v]) => set(k, v));
                  toast.success(`Filled ${Object.keys(fields).length} field(s) from paste`);
                }}
              />
              <div className="text-[10px] text-[#3A2A10] mt-2 leading-relaxed">
                1. Type the caliber, click <span className="font-mono-data">Emmy Watch</span>. 2. On the caliber page, press ⌘A then ⌘C. 3. Paste below. The parser extracts manufacturer, caliber, jewels, BPH, type, hands, years &amp; size (Ligne preferred over Diameter).
              </div>
            </div>
            {enrichSource && (
              <div className="text-[10px] text-[#3A2A10] mb-3 -mt-1">
                Suggested source:{" "}
                <a href={enrichSource} target="_blank" rel="noopener noreferrer"
                   className="text-[#D4AF37] hover:text-[#E5C158] underline">
                  {enrichSource.replace(/^https?:\/\//, "").split("/")[0]}
                </a>
              </div>
            )}
            <Row2>
              <Field label="Movement ID / Caliber *">
                <Input value={data.movement_id} onChange={(e) => set("movement_id", e.target.value)} className="dark-input" required data-testid="mvmnt-form-id" />
              </Field>
              <Field label="Manufacturer">
                <ComboSelect value={data.mvmnt_mfg} onChange={(v) => set("mvmnt_mfg", v)} options={validation["Mvmnt MFG"]} testid="mvmnt-form-mfg" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Year introduced">
                <Input type="number" value={data.year_introduced} onChange={(e) => set("year_introduced", e.target.value)} className="dark-input" data-testid="mvmnt-form-year-intro" />
              </Field>
              <Field label="Year discontinued">
                <Input type="number" value={data.year_discontinued} onChange={(e) => set("year_discontinued", e.target.value)} className="dark-input" data-testid="mvmnt-form-year-end" />
              </Field>
            </Row2>
          </Section>

          <Section title="Specifications">
            <Row2>
              <Field label="Movement Type">
                <ComboSelect value={data.movement_type} onChange={(v) => set("movement_type", v)} options={validation["Movement Type"]} testid="mvmnt-form-movement-type" />
              </Field>
              <Field label="Size (Ligne / American)">
                <ComboSelect value={data.size} onChange={(v) => set("size", v)} options={validation["Size"]} testid="mvmnt-form-size" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Jewels">
                <Input
                  value={data.jewels ?? ""}
                  onChange={(e) => set("jewels", e.target.value)}
                  className="dark-input"
                  placeholder="e.g. 17 or 7/17 or 17/21/25"
                  data-testid="mvmnt-form-jewels"
                />
              </Field>
              <Field label="BPH">
                <ComboSelect value={data.bph ? String(data.bph) : ""} onChange={(v) => set("bph", v)} options={validation["Beat Rate"]} testid="mvmnt-form-bph" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Lift Angle (°)">
                <Input
                  type="number"
                  step="0.5"
                  value={data.lift_angle ?? ""}
                  onChange={(e) => set("lift_angle", e.target.value)}
                  className="dark-input"
                  placeholder="52 (default)"
                  data-testid="mvmnt-form-lift-angle"
                />
              </Field>
              <Field label="Protection (Shock system)">
                <ComboSelect value={data.protection} onChange={(v) => set("protection", v)} options={validation["Shock Systems"]} testid="mvmnt-form-protection" />
              </Field>
            </Row2>
          </Section>

          <Section title="Hand Sizes (mm)">
            <Row2>
              <Field label="Hour hand"><Input type="number" step="0.01" value={data.hour_hand_mm} onChange={(e) => set("hour_hand_mm", e.target.value)} className="dark-input" data-testid="mvmnt-form-hour" /></Field>
              <Field label="Minute hand"><Input type="number" step="0.01" value={data.minute_hand_mm} onChange={(e) => set("minute_hand_mm", e.target.value)} className="dark-input" data-testid="mvmnt-form-minute" /></Field>
            </Row2>
            <Field label="Second hand">
              <Input type="number" step="0.01" value={data.second_hand_mm} onChange={(e) => set("second_hand_mm", e.target.value)} className="dark-input" data-testid="mvmnt-form-second" />
            </Field>
          </Section>

          <Section title="Description">
            <Textarea value={data.description} onChange={(e) => set("description", e.target.value)} className="dark-input min-h-24" data-testid="mvmnt-form-desc" placeholder="Construction, provenance, history…" />
          </Section>

          <Section title="Reference Link">
            <Input
              value={data.source_url}
              onChange={(e) => set("source_url", e.target.value)}
              className="dark-input"
              placeholder="https://emmywatch.com/en/m/eta-2892a2"
              data-testid="mvmnt-form-source-url"
            />
            {data.source_url && (
              <a
                href={data.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-[#D4AF37] hover:text-[#E5C158] underline inline-block mt-1.5"
              >
                Open: {data.source_url.replace(/^https?:\/\//, "").split("/")[0]}
              </a>
            )}
          </Section>

          <Section title="Notes">
            <Textarea value={data.notes} onChange={(e) => set("notes", e.target.value)} className="dark-input min-h-20" data-testid="mvmnt-form-notes" placeholder="Private notes" />
          </Section>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#8B6A1F]/35 sticky bottom-0 bg-[#FBF3D9] -mx-6 px-6 pb-2 pt-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-[#3A2A10] hover:text-[#0A0604]" data-testid="mvmnt-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] uppercase tracking-wider text-xs rounded-sm" data-testid="mvmnt-form-submit">
              {saving ? "Saving…" : movement ? "Save Changes" : "Add Movement"}
            </Button>
          </div>
        </form>

        <style>{`
          .dark-input {
            background-color: #F9F0D4 !important;
            border-color: rgba(139,106,31,0.25) !important;
            border-radius: 2px !important;
            color: #2B2214 !important;
          }
          .dark-input:focus, .dark-input:focus-visible {
            border-color: rgba(212,175,55,0.5) !important;
            outline: none !important;
            box-shadow: 0 0 0 1px rgba(212,175,55,0.3) !important;
          }
        `}</style>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-4 pb-2 border-b border-[#8B6A1F]/35">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/**
 * A single image slot for the movement form — preview pane, hidden file
 * input, Upload / Replace button, and Remove button. Two of these render
 * side-by-side to capture both the Bridge side and the Dial side.
 */
function MovementImageSlot({ label, testidPrefix, img, uploading, onPick, onClear, inputRef, onFile }) {
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-slot`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">{label}</div>
      <div
        className="relative w-full aspect-[4/3] bg-[#F9F0D4] border border-[#8B6A1F]/35 overflow-hidden cursor-pointer hover:border-[#D4AF37]/50 transition-colors"
        onClick={onPick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") onPick(); }}
        data-testid={`${testidPrefix}-preview`}
      >
        {img ? (
          <img src={img} alt={label} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#0A0604]/60">
            <ImageOff size={26} strokeWidth={1} />
            <span className="text-[10px] uppercase tracking-[0.25em] mt-2">Tap to upload</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 size={22} className="animate-spin text-[#D4AF37]" />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif"
        onChange={onFile}
        className="hidden"
        data-testid={`${testidPrefix}-input`}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={onPick}
          disabled={uploading}
          variant="outline"
          className="flex-1 h-8 bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm text-[10px] uppercase tracking-wider"
          data-testid={`${testidPrefix}-upload`}
        >
          <Upload size={12} className="mr-1.5" />
          {img ? "Replace" : "Upload"}
        </Button>
        {img && (
          <Button
            type="button"
            variant="ghost"
            onClick={onClear}
            className="h-8 px-2 text-[#3A2A10] hover:text-rose-500 hover:bg-transparent text-[10px] uppercase tracking-wider"
            data-testid={`${testidPrefix}-clear`}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
function Row2({ children }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">{label}</Label>
      {children}
    </div>
  );
}

import { parseMovementPaste, RANFFT_SEARCH, EMMY_SEARCH } from "@/lib/movementPaste";
import SearchableCombobox from "@/components/SearchableCombobox";

function ComboSelect({ value, onChange, options = [], testid }) {
  return (
    <SearchableCombobox value={value} onChange={onChange} options={options} testid={testid} />
  );
}

/**
 * Paste box for Emmy/Ranfft caliber text. Runs parseMovementPaste on
 * every input change so the preview of matched fields updates live.
 * Apply only sends non-empty values up to the parent.
 */
function PasteHelper({ onApply }) {
  const [text, setText] = useState("");
  const preview = text ? parseMovementPaste(text) : { parsed: {}, matched: [] };
  const count = preview.matched.length;
  return (
    <div className="space-y-2" data-testid="mvmnt-paste-helper">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste the selected text from the Emmy or Ranfft page here…"
        rows={3}
        className="w-full px-3 py-2 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-xs text-[#0A0604] placeholder:text-[#3A2A10]/60 focus:outline-none focus:border-[#D4AF37] resize-none font-mono-data"
        data-testid="mvmnt-paste-input"
      />
      {count > 0 && (
        <div className="flex items-start gap-2 text-[10px]">
          <div className="flex-1 flex flex-wrap gap-1">
            {preview.matched.map((k) => (
              <span key={k} data-testid={`mvmnt-paste-chip-${k}`} className="px-1.5 py-0.5 bg-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-sm font-mono-data text-[#0A0604]">
                {k.replace(/_/g, " ")}: {String(preview.parsed[k]).slice(0, 30)}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { onApply(preview.parsed); setText(""); }}
            className="shrink-0 px-3 h-7 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-[10px] uppercase tracking-wider rounded-sm"
            data-testid="mvmnt-paste-apply"
          >
            Apply {count} field{count > 1 ? "s" : ""}
          </button>
        </div>
      )}
      {text && count === 0 && (
        <div className="text-[10px] text-[#3A2A10] italic">No fields detected. Make sure you copied the spec table, not just the title.</div>
      )}
    </div>
  );
}
